import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { 
  Loader2,
  Save,
  Building2,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const AdminCompany = () => {
  const { api } = useAdmin();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadInfo();
  }, []);

  const loadInfo = async () => {
    try {
      const response = await api.getCompanyInfo();
      setInfo(response.data);
    } catch (error) {
      console.error('Failed to load company info:', error);
      toast.error('Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateCompanyInfo(info);
      toast.success('Company information saved');
    } catch (error) {
      console.error('Failed to save company info:', error);
      toast.error('Failed to save company information');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setInfo(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Information</h1>
          <p className="text-gray-600">Update your business contact details</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save className="mr-2" size={18} />}
          Save Changes
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="space-y-6">
          {/* Company Name */}
          <div>
            <Label htmlFor="name" className="flex items-center gap-2">
              <Building2 size={16} className="text-gray-400" />
              Company Name
            </Label>
            <Input
              id="name"
              value={info.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="address" className="flex items-center gap-2">
              <MapPin size={16} className="text-gray-400" />
              Address
            </Label>
            <Input
              id="address"
              value={info.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Contact */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone size={16} className="text-gray-400" />
                Phone Number
              </Label>
              <Input
                id="phone"
                value={info.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail size={16} className="text-gray-400" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={info.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Social Media */}
          <div className="pt-6 border-t">
            <h3 className="font-semibold text-gray-900 mb-4">Social Media</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="facebook" className="flex items-center gap-2">
                  <Facebook size={16} className="text-gray-400" />
                  Facebook URL
                </Label>
                <Input
                  id="facebook"
                  value={info.facebook}
                  onChange={(e) => handleChange('facebook', e.target.value)}
                  className="mt-1"
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <Label htmlFor="instagram" className="flex items-center gap-2">
                  <Instagram size={16} className="text-gray-400" />
                  Instagram URL
                </Label>
                <Input
                  id="instagram"
                  value={info.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  className="mt-1"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCompany;
